import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarIcon, ErrorState, LoadingState } from "@signet/ui";
import { useMe } from "@signet/web-kit";
import { daysBetween, melbourneToday, rateLabel } from "../shared/dates.ts";
import { dayText, exKeys, fetchUpcoming, price, whenLabel, type Item } from "./api.ts";
import { MarkPaidModal } from "./paid.tsx";

/* What runs out in the next week, overdue ones first, each with Mark as Paid (Jay, 2026-10-21). Now's Open button lands here. */

export const UPCOMING_ID = "ex-upcoming";

export function UpcomingPanel({ flash }: { flash: boolean }) {
  const { can } = useMe();
  const list = useQuery({ queryKey: exKeys.upcoming(7), queryFn: () => fetchUpcoming(7), refetchInterval: 60_000 });
  const [paying, setPaying] = useState<Item | null>(null);
  const today = melbourneToday();
  const items = list.data?.items ?? [];

  return (
    <section id={UPCOMING_ID} className={`panel ex-upcoming${flash ? " ex-flash" : ""}`}>
      <div className="panel-head">
        <h2>Coming up in the next week</h2>
      </div>
      <div className="panel-body flush">
        {list.isLoading ? (
          <LoadingState />
        ) : list.isError ? (
          <ErrorState title="Couldn't load what's coming up" onRetry={() => list.refetch()} />
        ) : !items.length ? (
          <p className="ex-quiet"><CalendarIcon size={14} /> Nothing runs out in the next week.</p>
        ) : (
          <div className="table">
            {items.map((item) => {
              const days = daysBetween(today, item.nextExpiry!);
              return (
                <div key={item.id} className="table-row ex-up-row">
                  <span className="table-cell">
                    <strong>{item.name}</strong>
                    {item.category ? <small>{item.category.name}</small> : null}
                  </span>
                  <span className="table-cell">
                    <strong className={days < 0 ? "ex-late" : days <= 1 ? "ex-soon" : undefined}>{whenLabel(days)}</strong>
                    <small>{dayText(item.nextExpiry!)}{item.rateCount && item.rateUnit ? `, every ${rateLabel(item.rateCount, item.rateUnit)}` : ""}</small>
                  </span>
                  <span className="num ex-up-price">{item.price === null ? "No price" : price(item.price, item.currency)}</span>
                  {can("expiries.payment:create") ? (
                    <button type="button" className="secondary-button compact" onClick={() => setPaying(item)}>Mark as Paid</button>
                  ) : <span />}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <MarkPaidModal item={paying} onClose={() => setPaying(null)} />
    </section>
  );
}
